import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { FileText, ClipboardList, Search, X } from 'lucide-react';
import BillingDetailSheet from '../../components/BillingDetailSheet';
import BillingCreateSheet from '../../components/BillingCreateSheet';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Billing = () => {
  const [tab, setTab] = useState('invoices');
  const [memberMap, setMemberMap] = useState({});
  const [invoices, setInvoices] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [selectedDoc, setSelectedDoc] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  // iter129 — Track whether the detail sheet should open in edit mode.
  const [selectedEditOnOpen, setSelectedEditOnOpen] = useState(false);

  useEffect(() => { refresh(); }, []);

  // Reopen the invoice/estimate that was open before a reload / app switch (its edit draft restores itself).
  const OPEN_DOC_KEY = 'nexflow_emp_billing_open_doc';
  const reopenedRef = React.useRef(false);
  useEffect(() => {
    if (reopenedRef.current || !(invoices.length || estimates.length)) return;
    reopenedRef.current = true;
    try {
      const ptr = JSON.parse(localStorage.getItem(OPEN_DOC_KEY) || 'null');
      const found = ptr && (ptr.type === 'estimate' ? estimates : invoices).find((d) => d.id === ptr.id);
      if (found) { setSelectedDoc(found); setSelectedType(ptr.type); if (ptr.type === 'estimate') setTab('estimates'); }
    } catch { /* ignore */ }
  }, [invoices, estimates]);
  useEffect(() => {
    if (!reopenedRef.current) return;
    try {
      if (selectedDoc?.id) localStorage.setItem(OPEN_DOC_KEY, JSON.stringify({ id: selectedDoc.id, type: selectedType }));
      else localStorage.removeItem(OPEN_DOC_KEY);
    } catch { /* storage unavailable */ }
  }, [selectedDoc, selectedType]);

  const refresh = async () => {
    try {
      const [i, e, m] = await Promise.all([
        axios.get(`${API}/invoices/all`, { withCredentials: true }),
        axios.get(`${API}/estimates/all`, { withCredentials: true }),
        axios.get(`${API}/memberships/active`, { withCredentials: true }),
      ]);
      setInvoices(i.data);
      setEstimates(e.data);
      const mm = {};
      m.data.forEach((mem) => { mm[mem.customer_id] = mem.tier; });
      setMemberMap(mm);
    } catch (err) { console.error(err); }
  };

  // iter236 — Search access for every active internal employee (office staff, techs, etc.).
  // Read-only filtering over the invoices/estimates the user is already permitted to load;
  // does NOT change field exposure or grant any new billing actions.
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [payFilter, setPayFilter] = useState('all');

  const payStateOf = (inv) => {
    const s = (inv.status || '').toLowerCase();
    if (s === 'refunded' || inv.payment_status === 'refunded') return 'refunded';
    const amt = Number(inv.amount || 0);
    const paid = Number(inv.amount_paid || 0);
    if (s === 'paid' || (amt > 0 && paid >= amt)) return 'paid';
    if (paid > 0) return 'partial';
    return 'unpaid';
  };

  const matchText = (doc) => {
    const t = q.trim().toLowerCase();
    if (!t) return true;
    const hay = [
      doc.invoice_number, doc.estimate_number, doc.customer_name, doc.tech_name,
      doc.work_order_number, doc.office_supplied_po, doc.po_number, doc.customer_po_number,
      doc.address, doc.service_address, doc.customer_address, doc.notes,
      doc.date ? new Date(doc.date).toLocaleDateString() : '', doc.service_date,
    ].filter(Boolean).join(' ').toLowerCase();
    return hay.includes(t);
  };

  const filteredInvoices = useMemo(() => invoices.filter((inv) => {
    if (!matchText(inv)) return false;
    if (statusFilter !== 'all' && (inv.status || '').toLowerCase() !== statusFilter) return false;
    if (payFilter !== 'all' && payStateOf(inv) !== payFilter) return false;
    return true;
  }), [invoices, q, statusFilter, payFilter]);

  const filteredEstimates = useMemo(() => estimates.filter((est) => {
    if (!matchText(est)) return false;
    if (statusFilter !== 'all' && (est.status || '').toLowerCase() !== statusFilter) return false;
    return true;
  }), [estimates, q, statusFilter]);

  const invStatuses = useMemo(() => Array.from(new Set(invoices.map((i) => (i.status || '').toLowerCase()).filter(Boolean))).sort(), [invoices]);

  const colorOf = (s) => ({
    paid: 'text-green-500 bg-green-500/10',
    approved: 'text-green-500 bg-green-500/10',
    pending: 'text-[#D4AF37] bg-[#D4AF37]/10',
    overdue: 'text-red-500 bg-red-500/10',
    rejected: 'text-red-500 bg-red-500/10',
  })[s] || 'text-[#A3A3A3] bg-[#262626]';

  const openDetail = (doc, type) => { setSelectedDoc(doc); setSelectedType(type); };

  const renderList = (items, Icon, prefix) => items.length === 0 ? (
    <div className="bg-[#141414] border border-[#262626] rounded-md p-8 text-center text-[#A3A3A3]">No {prefix.toLowerCase()}s yet</div>
  ) : (
    <div className="grid gap-3">
      {items.map((it) => (
        <button
          key={it.id}
          data-testid={`billing-card-${it.id}`}
          onClick={() => openDetail(it, prefix.toLowerCase())}
          className="w-full text-left bg-[#141414] border border-[#262626] hover:border-[#D4AF37] rounded-md p-4 flex items-center justify-between transition-all duration-200"
        >
          <div className="flex items-start gap-3">
            <Icon className="w-5 h-5 text-[#D4AF37] mt-1" />
            <div>
              <p className="text-white font-medium flex items-center gap-2 flex-wrap">
                {it.invoice_number || it.estimate_number} • {it.customer_name}
                {memberMap[it.customer_id] && (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#D4AF37] text-[#0A0A0A]">
                    {memberMap[it.customer_id].toUpperCase()} MEMBER
                  </span>
                )}
              </p>
              <p className="text-[#A3A3A3] text-sm line-clamp-1">{it.notes || it.description || (it.line_items?.[0]?.description) || ''}</p>
              <p className="text-[#A3A3A3] text-xs mt-1">{new Date(it.date).toLocaleDateString()}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[#D4AF37] text-xl font-bold">${it.amount?.toFixed(2)}</p>
            <span className={`inline-block mt-1 px-2 py-1 rounded text-xs ${colorOf(it.status)}`}>{it.status?.toUpperCase()}</span>
          </div>
        </button>
      ))}
    </div>
  );

  return (
    <div data-testid="employee-billing-page" className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-2">Billing</h2>
          <p className="text-[#A3A3A3]">Create invoices and estimates for customers</p>
        </div>
        {/* Shared create sheet — includes Quick Add Customer so techs can add a customer
            on the spot without leaving the invoice workflow. */}
        {/* Both sheets stay mounted so an unfinished invoice OR estimate draft restores itself. */}
        {['invoice', 'estimate'].map((t) => (
          <BillingCreateSheet
            key={t}
            type={t}
            trigger={tab === (t === 'invoice' ? 'invoices' : 'estimates')}
            onCreated={(newDoc) => {
              refresh();
              if (newDoc) {  // iter95 — auto-open detail for the new doc so techs can collect/send right away
                setSelectedDoc(newDoc);
                setSelectedType(t);
                setSelectedEditOnOpen(true);  // iter129 — start in edit mode after fresh create
              }
            }}
          />
        ))}
      </div>

      {/* iter236 — Read-only search available to every active internal employee */}
      <div className="flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-[#525252] absolute left-3 top-1/2 -translate-y-1/2" />
          <Input
            data-testid="billing-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customer, address, invoice #, WO#, PO#, technician, date…"
            className="pl-9 bg-[#0A0A0A] border-[#262626] text-white"
          />
          {q && (
            <button data-testid="billing-search-clear" onClick={() => setQ('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-[#525252] hover:text-white"><X className="w-4 h-4" /></button>
          )}
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger data-testid="billing-status-filter" className="bg-[#0A0A0A] border-[#262626] text-white w-full md:w-44"><SelectValue placeholder="Invoice status" /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-[#262626]">
            <SelectItem value="all" className="text-white">All statuses</SelectItem>
            {invStatuses.map((s) => <SelectItem key={s} value={s} className="text-white capitalize">{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={payFilter} onValueChange={setPayFilter}>
          <SelectTrigger data-testid="billing-payment-filter" className="bg-[#0A0A0A] border-[#262626] text-white w-full md:w-44"><SelectValue placeholder="Payment status" /></SelectTrigger>
          <SelectContent className="bg-[#141414] border-[#262626]">
            <SelectItem value="all" className="text-white">All payments</SelectItem>
            <SelectItem value="paid" className="text-white">Paid</SelectItem>
            <SelectItem value="partial" className="text-white">Partial</SelectItem>
            <SelectItem value="unpaid" className="text-white">Unpaid</SelectItem>
            <SelectItem value="refunded" className="text-white">Refunded</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#141414] border border-[#262626]">
          <TabsTrigger value="invoices" data-testid="tab-invoices" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-white">Invoices ({filteredInvoices.length})</TabsTrigger>
          <TabsTrigger value="estimates" data-testid="tab-estimates" className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] text-white">Estimates ({filteredEstimates.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="invoices" className="mt-6">{renderList(filteredInvoices, FileText, 'Invoice')}</TabsContent>
        <TabsContent value="estimates" className="mt-6">{renderList(filteredEstimates, ClipboardList, 'Estimate')}</TabsContent>
      </Tabs>

      <BillingDetailSheet
        open={!!selectedDoc}
        onOpenChange={(o) => { if (!o) { setSelectedDoc(null); setSelectedEditOnOpen(false); } }}
        doc={selectedDoc}
        type={selectedType}
        viewerRole="employee"
        editOnOpen={selectedEditOnOpen}
        onChange={refresh}
      />
    </div>
  );
};

export default Billing;
